// This module currently allows checking of permissions in a general way .
// This means any tryptich seperated by underscores will be interpreted as
// <allow/deny>_<resource>_<requested function>
// where
// *  allow/deny is either `allow` or `deny`
// *  resource is 'report' or 'issue' or 'form', or even something unique like
//     'submittedReport' (?) - note use of camelcase instead of another underscore.
// *  requested function is `view` or `edit` or `approve` or `render`, etc.
//
// To make use of the module, you call checkPermission() - see definition below.

const _require = require('lodash'),
  camelCase = _require.camelCase;

// here is an example layout of the permission object that should be passed in.
// const permissionExample = {
//   general: [
//     'report_approve',
//     'button_field_locking',
//     'report_edit_submitted',
//     'report_approve_own',
//     'dashboard',
//     'reportTag_edit',
//     'report_delete_draft',
//     'report_view_all',
//     'issue_show',
//     'job_edit',
//     'issue_admin',
//     'user_edit',
//     'picker_edit',
//     'report_process',
//     'adminPermission',
//   ],
//   summaries: [
//     'reportDateSummary',
//     'e33Environmental',
//     // ...
//   ],
//   reportTypes: {
//     general_safety_toolbox_talk_report: [
//       'report_edit_submitted',
//       'report_approve',
//     ],
//     pccpCorrectiveActionForm: [
//       'report_approve_own',
//       'report_approve',
//       'report_edit_submitted',
//     ],
//     incident_report: [
//       'report_edit_submitted',
//       'report_approve',
//     ],
//     // ...
//   },
//   formTypes: {
//     hull: [
//       'form_edit',
//       'form_view',
//     ],
//   },
//   adminSections: [
//     'reportTags',
//     'userGuide',
//     'jobs',
//     'summaries',
//     // ...
//   ],
//   adminSubsections: [
//     'employees_info',
//     // ...
//   ],
// };

const allowString = (resourceIdentifier, requestedFunction) => "allow_".concat(resourceIdentifier, "_").concat(requestedFunction);
const denyString = (resourceIdentifier, requestedFunction) => "deny_".concat(resourceIdentifier, "_").concat(requestedFunction);
const checkExistingPermissions = (resourceIdentifier, requestedFunction, permissions) => {
  const allowPermissionStr = allowString(resourceIdentifier, requestedFunction);
  if (permissions.indexOf(allowPermissionStr) > -1) {
    return true;
  }
  const denyPermissionStr = denyString(resourceIdentifier, requestedFunction);
  if (permissions.indexOf(denyPermissionStr) > -1) {
    return false;
  }
  const allowAllStr = allowString(resourceIdentifier, '*');
  if (permissions.indexOf(allowAllStr) > -1) {
    return true;
  }
  const denyAllStr = denyString(resourceIdentifier, '*');
  if (permissions.indexOf(denyAllStr) > -1) {
    return false;
  }
  return undefined;
};
const typeIdentifierFromResource = (resourceIdentifier, resource) => {
  const _resource = resource["".concat(resourceIdentifier, "Type")],
    _resource2 = _resource === void 0 ? {} : _resource,
    identifier = _resource2.identifier;
  return identifier;
};
const checkTypePermission = (resource, resourceIdentifier, requestedFunction, permissions) => {
  const typeIdentifier = typeIdentifierFromResource(resourceIdentifier, resource);
  const permissionSection = "".concat(resourceIdentifier, "Types");
  const _permissions$permissi = permissions[permissionSection],
    _permissions$permissi2 = _permissions$permissi === void 0 ? {} : _permissions$permissi,
    _permissions$permissi3 = _permissions$permissi2[typeIdentifier],
    typePermissions = _permissions$permissi3 === void 0 ? [] : _permissions$permissi3;

  // if there is at least one common permission - deny access
  return checkExistingPermissions(resourceIdentifier, requestedFunction, typePermissions);
};
const checkGeneralPermission = (resourceIdentifier, requestedFunctions, permissions) => {
  const _permissions$general = permissions.general,
    general = _permissions$general === void 0 ? [] : _permissions$general;
  const funcs = Array.isArray(requestedFunctions) ? requestedFunctions : [requestedFunctions];
  let result;
  funcs.forEach(func => {
    const tmp = checkExistingPermissions(resourceIdentifier, func, general);
    if (tmp !== undefined) {
      result = result || tmp;
    }
  });
  return result;
};
const getResourceIdentifier = resource => {
  const reportType = resource.reportType,
    formType = resource.formType,
    issueType = resource.issueType,
    resourceIdentifier = resource.resourceIdentifier;
  if (resourceIdentifier !== undefined) {
    return resourceIdentifier;
  }
  if (reportType !== undefined) {
    return 'report';
  }
  if (formType !== undefined) {
    return 'form';
  }
  if (issueType !== undefined) {
    return 'issue';
  }
  return undefined;
};

// resource is of the form:
// {
//   // ONE of these first four items -
//   formType: { identifier: '<id>' },
//   issueType: { identifier: '<id>' },
//   reportType: { identifier: '<id>' },
//   resourceIdentifier: '<resourceName>',

//   // and maybe...
//   status: { identifier: '<id>' }
// }
const checkPermission = (
// this is the report or issue or form.  It might need to include a
// (parsed) content field if you are using reportType permissions
resource,
// what do you want to do?  e.g. "edit"  Can be an array -
// if so, return true if ANY perm is true.
requestedFunctions,
// permission example is at the top of this module.
permissions,
// user object.
user,
// TBD.  this allows us to pass other important info.
environment) => {
  const resourceIdentifier = getResourceIdentifier(resource);
  const funcs = Array.isArray(requestedFunctions) ? requestedFunctions : [requestedFunctions];
  let result;
  funcs.forEach(requestedFunction => {
    // check form type based permission.
    let permissionResult = checkTypePermission(resource, resourceIdentifier, requestedFunction, permissions);
    if (permissionResult !== undefined) {
      if (result === undefined || result === false) {
        result = permissionResult;
      }
    } else {
      permissionResult = checkGeneralPermission(resourceIdentifier, requestedFunction, permissions, user, environment);
      if (permissionResult !== undefined) {
        if (result === undefined || result === false) {
          result = permissionResult;
        }
      }
    }
  });
  return result;
};
const makeResource = (resourceNameIn, typeIdentifier) => {
  const resourceName = camelCase(resourceNameIn);
  const resource = {
    resourceIdentifier: resourceName
  };
  if (typeIdentifier !== undefined) {
    resource["".concat(resourceName, "Type")] = {
      identifier: typeIdentifier
    };
  }
  return resource;
};
module.exports = {
  checkPermission,
  checkTypePermission,
  checkGeneralPermission,
  makeResource
};